import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

import { useAuth, NoMapsAccessError } from "../context/AuthContext";
import { Toaster } from "../components/common/Toaster";

import imgParada from "../assets/bg_image_auth_login.png";
import logo from "../assets/icon.png";
// Importación de los iconos para la clave
import eyeIcon from "../assets/eye.png";
import hiddenIcon from "../assets/hidden.png";

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false); // Estado para el ojito
  
  const [loginError, setLoginError] = useState(""); 
  const [toast, setToast] = useState({ show: false, message: "", type: "dangerColor" });

  const handleChange = (e) => {
    if (loginError) setLoginError(""); 
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const closeToast = () => setToast({ ...toast, show: false });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError(""); 

    if (!formData.email.trim() || !formData.password.trim()) {
      setLoginError("Por favor, complete todos los campos.");
      return;
    }

    setLoading(true);
    try {
      const { allowedRoles } = await login(formData.email, formData.password);

      // Con varios roles se le pregunta con cual quiere entrar; con uno solo AuthContext ya lo
      // dejo activo y no hay nada que preguntar.
      navigate(allowedRoles.length > 1 ? "/select-role" : "/tracking", { replace: true });
    } catch (error) {
      console.error("Login Error:", error);
      // Credenciales buenas pero ningun rol con acceso: hay que decirlo tal cual, porque
      // "credenciales incorrectas" manda a la persona a reintentar la clave para siempre.
      setLoginError(
        error instanceof NoMapsAccessError
          ? "Tu usuario no tiene un rol con acceso a maps. Contacta al administrador."
          : "Credenciales incorrectas. Intente nuevamente."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      transition={{ duration: 0.5 }}
      className="login-layout"
    >
      <style>{`
        .login-layout * { box-sizing: border-box; }

        .login-layout {
          display: flex;
          width: 100vw;
          height: 100vh;
          overflow: hidden;
          background-color: #FFFFFF;
          font-family: 'Poppins', sans-serif;
        }

        /* --- IZQUIERDA: IMAGEN Y BRANDING --- */
        .brand-panel {
          flex: 1;
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 80px;
          background: linear-gradient(180deg, 
            rgba(234, 192, 36, 0.0) 10%, 
            rgba(234, 192, 36, 0.6) 30%, 
            rgba(234, 192, 36, 1) 100%
          ), url(${imgParada});
          background-size: cover;
          /* POSICIÓN: center center para que no se vea tan abajo la imagen */
          background-position: center 15%; 
          background-repeat: no-repeat;
        }

        .brand-logo-absolute {
          position: absolute;
          top: 50px;
          left: 60px;
          width: 140px;
          height: auto;
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));
          z-index: 20;
        }

        .brand-content {
          position: relative;
          z-index: 10;
          padding-left: 20px;
        }

        .brand-title {
          font-size: 50px;
          font-weight: 800;
          line-height: 1.05;
          margin-bottom: 24px;
          color: #4B4A4A;
          letter-spacing: -1.5px;
          text-shadow: 0 2px 20px rgba(255,255,255,0.4);
        }

        .brand-subtitle {
          font-size: 20px;
          font-weight: 500;
          line-height: 1.6;
          max-width: 85%;
          color: #FFFFFF;
          text-shadow: 0 1px 4px rgba(0,0,0,0.2);
        }

        /* --- DERECHA: FORMULARIO --- */
        .form-panel {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          background-color: #FFFFFF;
          padding: 40px;
        }

        .form-container {
          width: 100%;
          max-width: 460px;
        }

        .form-header { margin-bottom: 30px; }
        .form-header h2 { font-size: 32px; font-weight: 700; color: #1F2937; margin-bottom: 10px; }
        .form-header p { font-size: 15px; color: #6B7280; }

        .input-group { margin-bottom: 20px; }
        .input-label { display: block; font-size: 14px; font-weight: 600; color: #374151; margin-bottom: 8px; }

        /* Contenedor relativo para el password y el ojito */
        .password-wrapper {
          position: relative;
          width: 100%;
        }

        .custom-input {
          width: 100%;
          padding: 16px 18px;
          font-size: 15px;
          color: #111827;
          background-color: #F9FAFB;
          border: 1px solid #E5E7EB;
          border-radius: 12px;
          transition: all 0.2s ease;
          outline: none;
        }

        .custom-input:focus {
          background-color: #FFFFFF;
          border-color: #EAC024;
          box-shadow: 0 0 0 4px rgba(234, 192, 36, 0.15);
        }

        /* Estilo del ojito */
        .toggle-password-icon {
          position: absolute;
          right: 16px;
          top: 50%;
          transform: translateY(-50%);
          width: 22px;
          height: 22px;
          cursor: pointer;
          opacity: 0.5;
          transition: opacity 0.2s;
          z-index: 5;
        }
        
        .toggle-password-icon:hover { opacity: 0.8; }

        .error-message-box {
          color: #DC2626;
          background-color: #FEF2F2;
          border: 1px solid #FECACA;
          padding: 10px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          margin-bottom: 15px;
          text-align: center;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }

        .submit-btn {
          width: 100%;
          padding: 16px;
          background-color: #EAC024;
          color: #FFFFFF;
          font-size: 16px;
          font-weight: 700;
          border: none;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 4px 6px rgba(234, 192, 36, 0.2);
        }

        .submit-btn:hover {
          background-color: #D9B01C;
          transform: translateY(-2px);
          box-shadow: 0 8px 12px rgba(234, 192, 36, 0.3);
        }

        .submit-btn:disabled {
          background-color: #E5E7EB;
          color: #9CA3AF;
          cursor: wait;
          transform: none;
          box-shadow: none;
        }

        .footer-copyright {
          margin-top: 40px;
          font-size: 12px;
          color: #D1D5DB;
          text-align: center;
        }

        /* RESPONSIVE CORREGIDO: En mobile no se ve nada de la izquierda */
        @media (max-width: 960px) {
          .brand-panel { display: none; }
          .form-panel { width: 100%; flex: none; height: 100vh; padding: 40px 24px; }
        }
      `}</style>

      {/* --- PANEL IZQUIERDO --- */}
      <div className="brand-panel">
        <img src={logo} alt="En La Parada Logo" className="brand-logo-absolute" />
        
        <div className="brand-content">
          <h1 className="brand-title">
            Agilizando rutas<br />
          </h1>
          <p className="brand-subtitle">
            La plataforma integral para la gestión y monitoreo de transporte en tiempo real.
          </p>
        </div>
      </div>

      {/* --- PANEL DERECHO --- */}
      <div className="form-panel">
        {toast.show && (
          <Toaster 
            textBody={toast.message} 
            color={toast.type} 
            close={closeToast} 
          />
        )}

        <div className="form-container">
          <div className="form-header">
            <h2>Bienvenido</h2>
            <p>Ingresa tus credenciales para acceder al panel.</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="input-group">
              <label className="input-label" htmlFor="email">Correo electrónico</label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="username" 
                className="custom-input"
                placeholder="nombre@empresa.com"
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            <div className="input-group">
              <label className="input-label" htmlFor="password">Contraseña</label>
              <div className="password-wrapper">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password" 
                  className="custom-input"
                  placeholder="••••••••••••"
                  value={formData.password}
                  onChange={handleChange}
                />
                {/* Icono de toggle para la clave */}
                <img 
                  src={showPassword ? eyeIcon : hiddenIcon} 
                  alt="Toggle Password" 
                  className="toggle-password-icon"
                  onClick={() => setShowPassword(!showPassword)}
                />
              </div>
            </div>

            {loginError && (
              <div className="error-message-box">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                {loginError}
              </div>
            )}

            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? "VALIDANDO..." : "INGRESAR"}
            </button>
          </form>

          <div className="footer-copyright">
            © {new Date().getFullYear()} En La Parada
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default Login;